import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthUser } from '@app/account/account-api';
import {
  SignalSummaryMap,
  TagSignalCount,
  TagSignalHistory,
} from '@app/profile/profile-api';
import { SearchTrackerService } from '@app/search/services/search-tracker.service';
import {
  MenuComponent,
  MenuViewModel,
} from '@app/shared/components/menu/menu.component';
import { AuthService } from '@app/shared/services/auth.service';
import { NotifierService } from '@app/shared/services/notifier.service';
import { SearchUrlService } from '@app/shared/services/search-url.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TrackingEventArgs } from '@app/shared/services/tracking';
import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import { pascalCaseKeys } from '@app/shared/utils/property';
import { WindowToken } from '@app/shared/window.token';
import { TagModalService } from '@app/tags/services/tag-modal.service';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api';
import { TranslateService } from '@ngx-translate/core';
import { TabRouterLessNavigationItem } from '@app/navigation/navigation';
import { Profile } from '@app/profile/profile';
import { TagActionOptionsService } from '@app/tags/services/tag-action-options.service';
import { ProfileFlagsService } from '@app/profile/services/profile-flags.service';

/**
 * Overview skills table shows list of top 6 skills and all focus skills
 *
 * @param skills {object} (required) object of focus and other (all) skills
 * @param isOwner {boolean} (required) boolean to show if user is looking at their own profile
 */

@Component({
  selector: 'dgx-overview-skills-table-v2',
  templateUrl: './overview-skills-table.component.html',
  styleUrls: ['./overview-skills-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverviewSkillsTableComponentV2 implements OnInit {
  @Input() public skills: TagsApi.FocusableTagDetails = {
    focus: [],
    other: [],
  };
  @Input() public isOwner: boolean;
  @Input() public owner: Profile;

  @Input() public signalCounts: TagSignalCount[];
  @Input() public signalHistories: TagSignalHistory[];
  @Input() public historyXAxisLabels: string[];
  @Input() public maxSkills: number;
  @Input() public signalSummaries: SignalSummaryMap;

  @ViewChild(MenuComponent) public menuRef: MenuComponent;

  public i18n = this.translate.instant([
    'Core_FocusSkill',
    'Core_FindContent',
    'Core_RemoveSkill',
    'DashboardNav_AddSkill',
    'dgTagRating_RecommendSkill',
    'Core_Empty_Image_Alt',
    'Core_Add',
    'Core_Top',
    'Core_Skills',
    'Core_ViewAll',
    'Core_Focus',
    'dgTagRating_AddRating',
    'dgTagRating_EditExpertise',
    'Core_SeeSkillCollection',
    'dgProfileOverview_FocusEmptyState_Viewer',
    'dgProfileOverview_FocusEmptyState_Owner',
    'dgProfileOverview_FocusEmptyState_Link',
    'dgTagRating_AddFocusSkill',
    'dgTagRating_RemoveFocusSkill',
    'Ext_TopicAddedSingle',
    'Core_Skills',
    'Core_Ratings',
    'Core_Signals',
    'dgProfileOverview_SkillSignalsTitle',
    'Core_Actions',
    'dgProfileOverview_SkillSignalDataTableLabel',
    'dgProfileOverview_ViewAllSkills',
  ]);
  public skillType: string = 'other';
  public authUser: AuthUser;
  public userLoggedIn: boolean = false;
  public currentSkill: string;
  public skillTab = {
    other: 'top',
    focus: 'focus',
  };
  public tabList: TabRouterLessNavigationItem[] = [
    {
      name: this.i18n.Core_Top,
      nameId: 'other',
    },
    {
      name: this.i18n.Core_Focus,
      nameId: 'focus',
    },
  ];

  public useSuggestedSkillsModal =
    this.profileFlagsService.showProfileSkillSuggestions;

  constructor(
    private tagsService: TagsService,
    private tagModalService: TagModalService,
    private tagRatingService: TagRatingService,
    private auth: AuthService,
    private searchUrlService: SearchUrlService,
    private searchTrackerService: SearchTrackerService,
    private translate: TranslateService,
    private webEnvironmentService: WebEnvironmentService,
    private trackerService: TrackerService,
    private element: ElementRef,
    @Inject(WindowToken) private windowRef: Window,
    private notifierService: NotifierService,
    private route: ActivatedRoute,
    private router: Router,
    private tagActionOptionsService: TagActionOptionsService,
    private profileFlagsService: ProfileFlagsService
  ) {}

  public get emptyImageUrl(): string {
    return this.webEnvironmentService.getBlobUrl(
      '/content/img/emptystate/profile-overview-focus-skills-empty-state.svg',
      true
    );
  }

  public ngOnInit(): void {
    this.authUser = this.auth.authUser;
    this.userLoggedIn = !!this.authUser;
    this.trackerService.setLocation('Profile');
    this.setupDefaultTab();
  }

  public popoverViewed(tag: TagsApi.TagDetails): void {
    this.trackerService.trackEventData({
      action: 'Skill Other Signals Previewed',
      element: this.element.nativeElement,
      properties: {
        SkillId: tag.tagId,
        SkillName: tag.name,
        IsFocusSkill: tag.requestingUserIsFocused,
        IsOnProfile: tag.isFollowing,
      },
    });
  }

  public getSignalCount(tagId): number {
    const index = this.signalCounts.findIndex((s) => s.tagId === tagId);
    let count = 0;

    if (index >= 0) {
      count = this.signalCounts[index].count;
    }

    return count;
  }

  public getSkillActionMenuLabel(skillName) {
    return this.translate.instant('dgProfileOverview_SkillActionsAriaLabel', {
      skillName,
    });
  }

  public addSkill(event: Event): void {
    this.trackEvent('Skill Adding Initiated');
    if (this.useSuggestedSkillsModal) {
      this.tagModalService
        .openAddSuggestedTagsModal(event)
        .subscribe(() =>
          this.notifierService.showSuccess(this.i18n.Ext_TopicAddedSingle)
        );
    } else {
      this.tagModalService.openAddTagsModal(event).subscribe(() => {
        this.trackEvent('Skill Adding Exited');
        this.notifierService.showSuccess(this.i18n.Ext_TopicAddedSingle);
      });
    }
  }

  public getMenuConfig(skill): MenuViewModel[] {
    return [
      this.tagActionOptionsService.getViewSkillSignalsMenuOption(
        skill,
        this.route
      ),
      {
        title: this.i18n.Core_FindContent,
        defaultAction: () => {
          this.tagsService.findContentForTag(skill);
        },
        isHidden: () =>
          !this.authUser ||
          this.authUser?.isSkillInventoryClient ||
          this.authUser?.isSkillAnalyticsClient,
      },
      {
        title: this.i18n.dgTagRating_RecommendSkill,
        defaultAction: () => {
          this.trackEvent('Skill Sharing Initiated', {
            SkillId: skill.tagId,
            SkillName: skill.title,
            IsFocusSkill: skill.userKeyIsFocused,
            IsOnProfile: this.isOwner,
          });
          // TODO: clean up `pascalCaseKeys`
          this.tagsService.recommendTag(pascalCaseKeys(skill)).subscribe();
        },
        isHidden: () =>
          !this.authUser?.canRecommendRatings ||
          this.authUser?.isSkillInventoryClient ||
          this.authUser?.isSkillAnalyticsClient ||
          !this.authUser.defaultOrgInfo,
        preventRefocus: true,
      },
      {
        title: this.i18n.dgTagRating_EditExpertise,
        defaultAction: (event) =>
          this.tagRatingService.openSkillModal(event, skill, false),
        isHidden: () => !this.isOwner,
        preventRefocus: true,
      },
      {
        title: this.i18n.DashboardNav_AddSkill,
        defaultAction: () =>
          this.tagsService
            .addUserTag({
              ...skill,
              requestingUserIsFocused: false,
              userKeyIsFocused: false,
              isFocused: false,
            })
            .subscribe(() => {
              this.notifierService.showSuccess(
                this.translate.instant('dgProfileOverview_AddSkill', {
                  skillName: skill.title,
                })
              );
            }),
        isHidden: () => this.isOwner,
      },
      {
        title: this.i18n.Core_RemoveSkill,
        defaultAction: () =>
          this.tagsService.removeUserTag(skill).subscribe(() => {
            this.notifierService.showSuccess(
              this.translate.instant('dgProfileOverview_RemoveSkill', {
                skillName: skill.title,
              })
            );
          }),
        isHidden: () => !skill?.isFollowing || !this.isOwner,
      },
      {
        title: this.i18n.dgTagRating_AddFocusSkill,
        defaultAction: (event: Event, popoverTrigger: ElementRef) =>
          this.tagsService.toggleUserInterestFocus(skill).subscribe(),
        isHidden: () =>
          skill.requestingUserIsFocused ||
          !this.authUser ||
          this.authUser?.isSkillInventoryClient ||
          this.authUser?.isSkillAnalyticsClient,
      },
      {
        title: this.i18n.dgTagRating_RemoveFocusSkill,
        defaultAction: (event: Event, popoverTrigger: ElementRef) =>
          this.tagsService.toggleUserInterestFocus(skill).subscribe(),
        isHidden: () =>
          !skill.requestingUserIsFocused ||
          !this.authUser ||
          this.authUser?.isSkillInventoryClient ||
          this.authUser?.isSkillAnalyticsClient,
      },
    ];
  }

  /**
   * Handle clicks on table rows
   *
   * NOTE: If items in a table row have their own click handlers, make sure they
   * use `event.stopPropagation()` to prevent the `rowClick` handler from firing.
   * @param event
   * @param skill
   */
  public clickHandler(event, skill: TagsApi.Tag): void {
    event.stopPropagation();
    this.goToSignals(event, skill);
  }

  public switchTab(skillType: string = 'other'): void {
    const profileOwnerId = this.isOwner ? 'self' : this.owner.id;
    if (skillType !== this.skillType) {
      this.trackEvent('Skills View Changed', {
        ViewChanged: 'tab',
        ChangedFrom: this.skillTab[this.skillType],
        ChangedTo: this.skillTab[skillType],
        SkillAddedCount: this.skills[skillType].slice(0, 6).length,
        SkillRatingsCount: this.getSkillRatingsCount(skillType, true),
        SkillOwnerUserId: profileOwnerId,
      });
      this.skillType = skillType;
    }
  }

  public getSkillRatingsCount(
    skillType: string = 'other',
    isTab: boolean
  ): number {
    const inputSkills = this.skills[skillType];
    const skills = isTab ? inputSkills.slice(0, 6) : inputSkills[skillType];
    return skills.reduce((count, skill) => {
      const completedRatings = skill.ratings.filter((r) => !!r.level);
      return count + completedRatings.length;
    }, 0);
  }

  public trackGoToAllSkills(): void {
    this.trackEvent('Skills View Changed', {
      ViewChanged: 'filter',
      ChangedFrom: this.skillTab[this.skillType],
      ChangedTo: 'view-all',
      SkillAddedCount: this.skills.other.length,
      SkillRatingsCount: this.getSkillRatingsCount('other', false),
      SkillOwnerUserId: this.owner.id,
    });
  }

  public trackMenuOpen($event: Event, skill: TagsApi.TagDetails): void {
    $event.preventDefault();
    $event.stopPropagation();
    if (!this.menuRef.menuPopoverRef.isPopoverPresent) {
      this.trackEvent('Skills Menu Opened', {
        SkillId: skill.tagId,
        SkillName: skill.title,
        IsFocusSkill: skill.userKeyIsFocused,
        IsOnProfile: this.isOwner,
      });
    }
  }

  public setCurrentSkill(event: MouseEvent, skill?: TagsApi.Tag): void {
    this.currentSkill = skill?.title;
  }

  public goToSignals(event: Event, skill: TagsApi.Tag): void {
    event.stopPropagation();
    const url = this.router
      .createUrlTree(['../signals'], {
        relativeTo: this.route,
        queryParams: { tagId: skill.tagId },
      })
      .toString();
    this.router.navigateByUrl(url);
  }

  private setupDefaultTab(): void {
    if (
      this.authUser?.setOverviewTabDefault?.toLowerCase() ===
      this.skillTab.focus
    ) {
      this.skillType = 'focus';
    }
  }

  private trackEvent(
    action: string,
    properties?: { [name: string]: any }
  ): void {
    let data: TrackingEventArgs = { action };
    // add properties, if defined
    if (properties) {
      data = {
        ...data,
        properties,
      };
    }
    // track event data
    this.trackerService.trackEventData(data);
  }
}
